import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import Header from './Header';
import CookieConsentBanner from './CookieConsentBanner';
import { Linkedin, Mail, Facebook, Instagram, Youtube, X } from 'lucide-react';

const MainLayout = ({ children }) => {
  const location = useLocation();
  
  return (
    <div className="main-layout min-h-screen flex flex-col">
      <Header currentPath={location.pathname} />
      <main className="main-content flex-grow">
        {children}
      </main>
      
      {/* Footer */}
      <footer className="bg-slate-900 text-gray-300 pt-12 pb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Upper Footer Section - 4 Columns */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6 mb-10">

            {/* Column 1: Logo, Brief Description, Social Media */}
            <div className="space-y-3 md:col-span-2 lg:col-span-1">
              <Link to="/" aria-label="Back to homepage" className="inline-block mb-8">
                <img src="/promotly-logo-transparent.png" alt="PROMOTLY" className="h-16 w-auto filter brightness-0 invert" />
              </Link>
              <p className="text-sm text-gray-300 leading-relaxed">
                Promotly empowers businesses to build lasting customer relationships through innovative AI-driven loyalty solutions.
              </p>
              <p className="text-xs text-gray-400">
                Promotly — enterprise loyalty &amp; rewards infrastructure by Sharee Coin, Cairo, since 2018.
              </p>
              <div className="flex space-x-3 mt-3">
                <a href="https://www.linkedin.com/company/sharee-coin" target="_blank" rel="noopener noreferrer" aria-label="Sharee Coin on LinkedIn" className="text-gray-300 hover:text-teal-400 transition-colors">
                  <Linkedin size={22} />
                </a>
                <a href="https://www.facebook.com/ShareeCoin" target="_blank" rel="noopener noreferrer" aria-label="Sharee Coin on Facebook" className="text-gray-300 hover:text-teal-400 transition-colors">
                  <Facebook size={22} />
                </a>
                <a href="https://www.instagram.com/shareecoin" target="_blank" rel="noopener noreferrer" aria-label="Sharee Coin on Instagram" className="text-gray-300 hover:text-teal-400 transition-colors">
                  <Instagram size={22} />
                </a>
                <a href="https://www.youtube.com/channel/UC1n22N5BTnsGCklkmw_lMGw" target="_blank" rel="noopener noreferrer" aria-label="Sharee Coin on YouTube" className="text-gray-300 hover:text-teal-400 transition-colors">
                  <Youtube size={22} />
                </a>
                <a href="https://x.com/ShareeCoin" target="_blank" rel="noopener noreferrer" aria-label="Sharee Coin on X" className="text-gray-300 hover:text-teal-400 transition-colors">
                  <X size={22} />
                </a>
              </div>
            </div>

            {/* Column 2: Product Links */}
            <div>
              <h4 className="text-base font-semibold text-gray-100 mb-4">Product</h4>
              <ul className="space-y-2.5 text-sm font-medium">
                <li><a href="/features" className="text-gray-300 hover:text-teal-400 transition-colors">Features</a></li>
                <li><a href="/pricing" className="text-gray-300 hover:text-teal-400 transition-colors">Pricing</a></li>
                <li><a href="/integrations" className="text-gray-300 hover:text-teal-400 transition-colors">Integrations</a></li>
                <li><a href="/api-docs" className="text-gray-300 hover:text-teal-400 transition-colors">Developers</a></li>
              </ul>
            </div>

            {/* Column 3: Company/Resources Links */}
            <div>
              <h4 className="text-base font-semibold text-gray-100 mb-4">Company</h4>
              <ul className="space-y-2.5 text-sm font-medium">
                <li><a href="/about" className="text-gray-300 hover:text-teal-400 transition-colors">About Us</a></li>
                <li><a href="https://blog.promotly.cloud" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-teal-400 transition-colors">Blog/Resources</a></li>
                <li><a href="/careers" className="text-gray-300 hover:text-teal-400 transition-colors">Careers</a></li>
              </ul>
            </div>

            {/* Column 4: Support & Legal Links */}
            <div>
              <h4 className="text-base font-semibold text-gray-100 mb-4">Support &amp; Legal</h4>
              <ul className="space-y-2.5 text-sm font-medium">
                <li><Link to="/help" className="text-gray-300 hover:text-teal-400 transition-colors">Help Center</Link></li>
                <li><a href="/contact" className="text-gray-300 hover:text-teal-400 transition-colors">Contact Us</a></li>
                <li><a href="/privacy-policy" className="text-gray-300 hover:text-teal-400 transition-colors">Privacy Policy</a></li>
                <li><a href="/refund-cancellation-policy" className="text-gray-300 hover:text-teal-400 transition-colors">Refund &amp; Cancellation Policy</a></li>
                <li><a href="/terms" className="text-gray-300 hover:text-teal-400 transition-colors">Terms of Service</a></li>
              </ul>
              <div className="mt-5 text-sm font-medium">
                <p className="flex items-center gap-2 text-gray-300"><Mail size={14} className="text-teal-400" /> info@shareecoin.com</p>
              </div>
            </div>

          </div>

          {/* Trust Bar */}
          <div className="bg-slate-800 border border-teal-800/60 rounded-xl px-4 py-3.5 mb-5 flex flex-wrap items-center justify-center gap-x-8 gap-y-2">
            <span className="text-sm font-semibold text-teal-300">Data Residency</span>
            <span className="text-sm font-semibold text-teal-300">GDPR Ready</span>
            <span className="text-sm font-semibold text-teal-300">99.9% Uptime</span>
            <span className="text-sm font-semibold text-teal-300">Enterprise Encryption</span>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-slate-700 pt-5 text-center">
            <p className="text-sm text-gray-400">&copy; {new Date().getFullYear()} Sharee Coin for Administrative and Marketing Services. Promotly is a product of Sharee Coin. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <CookieConsentBanner />
    </div>
  );
};

export default MainLayout; 